import {
  createFileRoute, Link, Outlet, redirect, useChildMatches, useNavigate, useRouterState,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { engagementsQuery, meQuery, staleQuery } from '../api/jedi'
import { NavRail } from '../shell/NavRail'
import {
  INTO_COLD_START, NewEngagementDialog, canCreateEngagement, useIntoColdStart,
} from '../shell/NewEngagementDialog'
import { TopBar } from '../shell/TopBar'
import { WakingUp } from '../shell/WakingUp'
import { ChatPanel } from '../shell/ChatPanel'
import { FeedbackDialog, type FeedbackContext } from '../shell/FeedbackDialog'
import { ChatContext, type ChatAbout, type ChatRecord } from '../shell/chatContext'
import { RAIL_NAV } from '../shell/railNav'
import type { ChatSeed } from '../shell/useConversation'
import { conversationKey, existingThread } from '../shell/conversationStore'
import { useRailHotkeys } from '../shell/useRailHotkeys'
import { settle, switchEngagement, useEngagement, useStoredEngagement } from '../lib/engagement'
import { isWaking } from '../lib/waking'
import { activeAccount, ensureInitialized } from '../auth/msal'

export const Route = createFileRoute('/_shell')({
  // The auth gate for every surface: no account, no shell (ADR-0008).
  beforeLoad: async () => {
    await ensureInitialized()
    if (!activeAccount()) throw redirect({ to: '/login' })
  },
  component: ShellLayout,
})

/**
 * Routes that need no engagement. Both are instruments rather than surfaces,
 * and administration is how the first engagement in an environment gets made,
 * so it has to open for somebody who is on none. Its Demos page likewise: in a
 * deployment that exists to present, the first engagement anybody is on is the
 * first demo they set up there.
 *
 * Matched by ROUTE, never by URL. The URL changes the moment a link is
 * clicked, while the outlet goes on rendering the previous route until the
 * next one's code has loaded, so a URL check opened the outlet onto Home for a
 * person with no engagement and it threw. Found by the browser harness.
 */
const ENGAGEMENT_FREE = new Set<string>([
  '/_shell/admin', '/_shell/admin_/demos', '/_shell/flags', '/_shell/servicenow-docs',
])

/**
 * THE ENGAGEMENT GATE (found on test, 2026-09-17). Nothing engagement-scoped
 * renders until the engagement it will read is one the caller is on.
 *
 * Before this, the shell rendered straight away against whatever
 * `lib/engagement` held, and for anybody with nothing stored that was
 * `cascade`. People added only to other engagements opened the app on a door
 * that refused them: every read came back 403, Home rendered blank because it
 * shows no errors, and the switcher displayed their own engagement as though it
 * were already open. The fix is an ORDER rather than a fallback: read the
 * directory, settle on an engagement in it, then render.
 *
 * Waits on the directory only, not on /me. Both bind invitations on the server
 * now, so neither has to run first, and the common path costs no extra trip.
 */
function ShellLayout() {
  const directory = useQuery(engagementsQuery)
  const stored = useStoredEngagement()
  const settled = directory.data ? settle(directory.data, stored) : null
  // What the outlet below will actually render, read from the same matches it
  // renders from.
  const child = useChildMatches({ select: (matches) => matches[0]?.routeId })

  // Remember what was settled: a first visit, somebody removed from what they
  // had open, or a rename. An effect because switching notifies every
  // subscriber, which must not happen during render.
  const settledId = settled?.id
  const settledName = settled?.name
  useEffect(() => {
    if (settledId && settledName && (settledId !== stored?.id || settledName !== stored?.name)) {
      switchEngagement({ id: settledId, name: settledName })
    }
  }, [settledId, settledName, stored?.id, stored?.name])

  // A refetch that fails keeps the data it had, so only a directory that never
  // loaded is a reason to stop.
  //
  // AND THE WAIT ITSELF IS NOW A SCREEN. This branch used to render an empty
  // `div.shell`, which against the default dark theme is the black screen people
  // were left looking at for as long as it took a scaled-to-zero API to start.
  // `WakingUp` holds that blank for its first moment, so a warm load is
  // unchanged, and then says what is happening. A wake that outlived its retry
  // budget belongs to the same screen rather than to `Unreachable`, whose
  // sentence ("did not load, reload the page") is the wrong advice for an
  // environment that is still coming up on its own.
  if (!directory.data) {
    if (directory.isError && !isWaking(directory.error)) {
      return <Unreachable error={directory.error} />
    }
    // A RETRY IN FLIGHT IS A WAKE AGAIN, not a failure still. React Query keeps
    // `isError` true across a refetch of an errored query, so reading it alone
    // left "Try again" rendering the identical screen it was clicked on, with
    // nothing to show that anything had happened for the whole budget. That is
    // the same shape of defect as the Snooze button that had already committed
    // (see `auth/apiFetch`), and it is a press with no answer.
    return (
      <WakingUp
        error={directory.isError && !directory.isFetching ? directory.error : null}
        onRetry={() => void directory.refetch()}
      />
    )
  }

  if (!settled) {
    return child && ENGAGEMENT_FREE.has(child) ? (
      <div className="shell">
        <main className="shell-content">
          <Outlet />
        </main>
      </div>
    ) : (
      <NoEngagement />
    )
  }

  // One render while the effect above stores what was settled.
  if (settled.id !== stored?.id) return <div className="shell" />

  return <Shell />
}

/**
 * The application frame, re-expressed from the ShellNavigation contract
 * (never ported from the canvas): the eight-item rail in the ratified order
 * with per-nav attention counts, the Upload affordance routing to Inputs
 * (G-43's default), the content slot, and the chat. The chat is the phase-1
 * shape ruling's panel rather than the chat-form surface, and since 2026-09-21
 * it FLOATS: it lies over the bar and the content and never moves either
 * (ruled by Erik for every chat surface). Every door opens the same panel
 * through the context.
 *
 * Wave 13 adds the TOPBAR, and with it the bell tray and the engagement
 * switcher, which moved up out of the rail where wave 4 parked them. The
 * second topbar image brings the bar to the reference: the switcher's menu,
 * the metrics strip in the design's order, and New and Export, which are
 * present and inert until there is anything behind them.
 */
function Shell() {
  const { id: engagementId } = useEngagement()
  const navigate = useNavigate()
  /*
    PLAN'S COUNT (nav-attention-counts §3; RFC-0012's 2026-10-20 amendment):
    the plan items whose dates are older than the evidence, the same query
    Plan's banner and filter read. It is a standing count: it clears when the
    plan is restated, never when the page is looked at.
  */
  const stale = useQuery(staleQuery(engagementId))
  const staleCount = Array.isArray(stale.data) ? stale.data.length : 0
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  /*
    WHOSE TODAY THE SCREEN IS SHOWING (the PM view, ruled 2026-09-22). Work
    states its scope in the URL, so the chat reads it from there rather than
    from the route's own state: the panel is mounted by the shell and the
    selector lives on the surface, and a shared store between them would be a
    second place for the same fact to live.
  */
  const who = useRouterState({
    select: (state) => {
      const stated = (state.location.search as { who?: unknown }).who
      return typeof stated === 'string' && stated.length > 0 ? stated : null
    },
  })
  const [chatOpen, setChatOpen] = useState(false)
  const [about, setAbout] = useState<ChatAbout>({ kind: 'surface' })
  const [trayOpen, setTrayOpen] = useState(false)
  const [focusSetId, setFocusSetId] = useState<string | null>(null)
  const [seed, setSeed] = useState<ChatSeed | null>(null)
  // The record whose history panel is open, announced by the panel while it is
  // mounted. A ref, not state: nothing renders from it, the rail's door reads it
  // at the moment it is pressed.
  const selected = useRef<ChatRecord | null>(null)
  const composer = useRef<HTMLInputElement | null>(null)
  const chatIsOpen = useRef(false)
  useEffect(() => {
    chatIsOpen.current = chatOpen
  }, [chatOpen])

  // A conversation is engagement-scoped: switching engagements closes it and
  // clears its subject, seed and focus, so a conversation never crosses the
  // wall. The key on ChatPanel below makes any reopened panel a fresh session.
  useEffect(() => {
    setChatOpen(false)
    setTrayOpen(false)
    setFocusSetId(null)
    setSeed(null)
    setAbout({ kind: 'surface' })
  }, [engagementId])

  // One layer at a time on the right-hand edge: opening the chat closes the
  // tray, whose bell the panel lies over anyway.
  const show = useCallback((next: ChatAbout, focus: string | null) => {
    setAbout(next)
    setFocusSetId(focus)
    setSeed(null)
    setTrayOpen(false)
    setChatOpen(true)
  }, [])
  const open = useCallback((setId?: string) => show({ kind: 'surface' }, setId ?? null), [show])
  // The rail's door (ruled 2026-09-21: "The record"). With a record's history
  // panel open it takes that record; otherwise the surface. With the panel
  // already open it keeps the conversation and only moves focus to it.
  const openRail = useCallback(() => {
    if (chatIsOpen.current) {
      composer.current?.focus()
      return
    }
    const record = selected.current
    show(record ? { kind: 'record', ...record } : { kind: 'surface' }, null)
  }, [show])
  // A door's pre-fill (C-033 entry point 2). The nonce re-arms the seed when
  // the same door is used twice.
  const openSeeded = useCallback((text: string, answersQuestionId?: string) => {
    setAbout({ kind: 'surface' })
    setFocusSetId(null)
    setSeed((current) => ({ text, nonce: (current?.nonce ?? 0) + 1, answersQuestionId }))
    setTrayOpen(false)
    setChatOpen(true)
  }, [])
  // A prompt (Home's Go deeper): the same opening as a pre-fill, and the
  // conversation sends it rather than leaving it in the box.
  const ask = useCallback((text: string) => {
    setAbout({ kind: 'surface' })
    setFocusSetId(null)
    setSeed((current) => ({ text, nonce: (current?.nonce ?? 0) + 1, ask: true }))
    setTrayOpen(false)
    setChatOpen(true)
  }, [])
  // The record door (029 §3, RFC-0015 §4a): the chat opens with the record in
  // scope, so "it" means that record.
  const openRecord = useCallback((record: ChatRecord) => show({ kind: 'record', ...record }, null), [show])
  const selectRecord = useCallback((record: ChatRecord | null) => {
    selected.current = record
  }, [])
  const chat = useMemo(
    () => ({ open, openRail, openSeeded, ask, openRecord, selectRecord }),
    [open, openRail, openSeeded, ask, openRecord, selectRecord],
  )

  /*
    The keyboard layer (nav-attention-counts §6). It lives here rather than in
    the rail because two of its keys are not the rail's business: Esc closes
    the chat and the tray, which this component owns.
  */
  const closeLayers = useCallback(() => {
    setChatOpen(false)
    setTrayOpen(false)
  }, [])
  const goInputs = useCallback(() => {
    void navigate({ to: '/inputs' })
  }, [navigate])
  /*
    FEEDBACK (ADR-0065): a dialog over the page, and where the person was is
    read at the moment they open it, not when they send: the record panel can
    close while they type, and what they are talking about is what was in front
    of them when they pressed the key.
  */
  const [feedback, setFeedback] = useState<FeedbackContext | null>(null)
  const openFeedback = useCallback(() => {
    const record = selected.current
    const surface = surfaceKeyFor(pathname)
    /*
      THE EXCHANGE IT MAY BE ABOUT (ADR-0065 decision 6). Feedback on an
      answer is only useful to the evals if someone can find the answer, so the
      conversation the person was in goes with it, by id. Which one: the open
      chat's; else the open record's, which its drawer pane shares; else the
      surface's. Only a thread that has turns in it counts, and the thread is
      looked up, never made, so pressing F does not mint a conversation.
    */
    const scopeRef = chatIsOpen.current ? (about.kind === 'record' ? about.scopeRef : undefined) : record?.scopeRef
    const thread = existingThread(
      conversationKey({ engagementId, scopeRef, surface: surface ?? undefined, who: who ?? undefined }),
    )
    const spoken = thread && thread.entries.length > 0 ? thread : undefined
    const lastSet = spoken ? [...spoken.entries].reverse().find((entry) => entry.setId)?.setId : undefined
    setFeedback({
      route: pathname,
      surface,
      surfaceLabel: surfaceLabelFor(pathname),
      engagementId: engagementId ?? null,
      recordRef: record?.scopeRef ?? null,
      recordName: record ? (record.shortRef ? `${record.shortRef} ${record.label}` : record.label) : null,
      conversationId: spoken?.conversationId ?? null,
      setId: (chatIsOpen.current ? focusSetId : null) ?? lastSet ?? null,
    })
  }, [pathname, engagementId, about, who, focusSetId])
  useRailHotkeys({ onChat: openRail, onUpload: goInputs, onFeedback: openFeedback, onEscape: closeLayers })

  return (
    <ChatContext.Provider value={chat}>
      <div className="shell">
        <NavRail
          attention={staleCount > 0 ? { plan: { n: staleCount, kind: 'stale' } } : {}}
          onFeedback={openFeedback}
        />
        <div className="shell-body">
          {/* The tray is mounted BY the bar now, because it hangs from the
              bell. `trayOpen` stays here: Esc closes every layer, and that is
              the shell's to own, not the bar's. */}
          <TopBar
            onOpenTray={() => setTrayOpen((open) => !open)}
            onCloseTray={() => setTrayOpen(false)}
            trayOpen={trayOpen}
          />
          <main className="shell-content">
            <Outlet />
          </main>
        </div>
        {chatOpen && (
          <ChatPanel
            key={`${engagementId}:${about.kind === 'record' ? about.scopeRef : 'surface'}`}
            about={about}
            surfaceLabel={surfaceLabelFor(pathname)}
            surfaceKey={surfaceKeyFor(pathname)}
            who={who}
            focusSetId={focusSetId}
            seed={seed}
            inputRef={composer}
            onClose={() => setChatOpen(false)}
          />
        )}
        {feedback && (
          <FeedbackDialog open context={feedback} onClose={() => setFeedback(null)} />
        )}
      </div>
    </ChatContext.Provider>
  )
}

/**
 * The rail's own name for where the person is, `Work` or `Plan`, read from the
 * same table the rail renders and the number keys route by. Null off the rail
 * (Administration, cold start, runs, flags), where the chat names the
 * engagement instead.
 */
function surfaceFor(pathname: string) {
  return RAIL_NAV.find((entry) =>
    entry.to === '/' ? pathname === '/' : pathname === entry.to || pathname.startsWith(`${entry.to}/`),
  )
}

function surfaceLabelFor(pathname: string): string | null {
  return surfaceFor(pathname)?.label ?? null
}

/** The surface as the rail keys it (`work`), which the chat's request carries (wave 23). */
function surfaceKeyFor(pathname: string): string | null {
  return surfaceFor(pathname)?.id ?? null
}

/**
 * Nobody has added this person to an engagement, or they were added under an
 * address that is not the one they sign in with. From here the two look the
 * same, so the page names the address the server binds on (`/me`'s `upn`, read
 * from the same claim the binding reads) and lets the person carry it to
 * whoever runs the environment. On test that one line would have ended a
 * search: a grant was one letter off the address its person signs in with.
 *
 * No rail. Its Chat and Upload need an engagement, and a control that does
 * nothing when pressed is worse than one that is not there.
 *
 * And no topbar, so no switcher, which is where New engagement lives
 * everywhere else. Somebody who can create one therefore gets the same dialog
 * from here, or the first engagement in a fresh environment could only be made
 * by knowing to go to Administration. Administration stays linked beside it,
 * because it is also where the people get added.
 */
function NoEngagement() {
  const me = useQuery(meQuery)
  const upn = me.data?.upn
  const intoColdStart = useIntoColdStart()
  const [creating, setCreating] = useState(false)

  return (
    <div className="shell">
      <main className="shell-content">
        <section className="surface">
          <header className="surface-head">
            <h1>No engagements yet</h1>
            <p className="surface-sub">
              {upn ? (
                <>
                  You are signed in as <strong>{upn}</strong>, and that address is not on any
                  engagement yet.
                </>
              ) : (
                'You are signed in, and you are not on any engagement yet.'
              )}
            </p>
          </header>
          <p className="surface-note">
            Ask whoever runs J.E.D.I. here to add you. If they already have, check the address
            they used: it has to be the one you sign in with.
          </p>
          {canCreateEngagement(me.data) && (
            <>
              <p className="surface-note">
                You can set one up yourself in <Link to="/admin">Administration</Link>.
              </p>
              <button type="button" className="dialog-primary" onClick={() => setCreating(true)}>
                New engagement
              </button>
              <NewEngagementDialog
                open={creating}
                onClose={() => setCreating(false)}
                onCreated={intoColdStart}
                next={INTO_COLD_START}
              />
            </>
          )}
        </section>
      </main>
    </div>
  )
}

function Unreachable({ error }: { error: Error }) {
  return (
    <div className="shell">
      <main className="shell-content">
        <section className="surface">
          <header className="surface-head">
            <h1>Your engagements did not load</h1>
            <p className="surface-sub">Reload the page to try again.</p>
          </header>
          <p className="surface-note">{error.message}</p>
        </section>
      </main>
    </div>
  )
}
